/**
 * Normalize a phone number to E.164 format.
 *
 * – If it starts with "00" or "+", we treat it as already international (just strip any
 *   stray trunk‑zero for your default country).
 * – If it starts with your default country code (e.g. "31"), we assume the user already
 *   supplied a country code and just prefix "+" (dropping one trunk‑zero if mistakenly left).
 * – Otherwise, if it starts with a "0", we take that as a national number and prepend "+<defaultCountryCode>".
 * – Anything else we assume is already an international NSN (no leading zero), so we just prefix "+".
 *
 *
 * Examples (defaultCountryCode = "31"):
 *   "0627009890"    → "+31627009890"      (national)
 *   "+31627009890"  → "+31627009890"      (already E.164)
 *   "0031627009890" → "+31627009890"      (00 → +)
 *   "31627009890"   → "+31627009890"      (already CC, no +)
 *   "0418661337"    → "+31418661337"      (national)
 *   "+441632960960" → "+441632960960"     (other CC)
 *
 * @param {string} input
 * @param {string} defaultCountryCode — e.g. "31" for Netherlands
 * @returns {string} E.164‑formatted phone number (or `""` if invalid)
 */
export function normalizePhoneNumber(input: string, defaultCountryCode = "31") {
  let num = input.trim().replace(/[\s\-().]/g, "")

  // 1) convert leading "00" → "+"
  if (num.startsWith("00")) {
    num = "+" + num.slice(2)
  }

  // 2) if it starts with "+", it's already international
  if (num.startsWith("+")) {
    // optionally strip a trunk‑zero if someone wrote "+31 0…"
    const trunkRe = new RegExp(`^\\+${defaultCountryCode}0(\\d+)$`)
    if (trunkRe.test(num)) {
      num = num.replace(trunkRe, `+${defaultCountryCode}$1`)
    }
    return num
  }

  // 3) if it starts with your default CC digits (e.g. "31"), assume they meant "+31…"
  if (num.startsWith(defaultCountryCode)) {
    // drop one "0" if they wrote "3106…" instead of "316…"
    const ccRe = new RegExp(`^${defaultCountryCode}0?(\\d+)$`)
    const m = num.match(ccRe)
    if (m) {
      return "+" + defaultCountryCode + m[1]
    }
    return "+" + num
  }

  // 4) national format: leading "0" → strip it and prepend your default CC
  if (num.startsWith("0")) {
    return "+" + defaultCountryCode + num.slice(1)
  }

  // 5) fallback: assume it's already the full NSN of some country, just prefix "+"
  return "+" + num
}
